import { Box, Typography, Button, Chip } from "@mui/material";
import Breadcrumb from "@/components/Breadcrumb";
import {
  DefaultPrimaryButton,
  DefaultSecondaryButton,
} from "@/styles/globalstyles";
import BackButton from "@/components/buttons/BackButton";
import AuthorizationManageTable from "./AuthorizationManageTable";
import { useEffect, useState } from "react";
import SaveRoleModal from "./SaveRoleModal";
import {
  useGetAllResources,
  useGetAllGroups,
  useGetAllActions,
  useGetGroupResourceActionMapping,
  useInsertGroup,
} from "@/hooks/groupHook";
import { useQueryHandler } from "@/hooks/useQueryHandler";
import type { UpdateGroupParams } from "@/api/groupService";
import { useUpdateGroup } from "../../../hooks/groupHook";
import { getAxiosErrorMessage } from "@/utils/healper";
import { usePermissions } from "@/hooks/usePermissions";

const AuthorizationManagement = () => {
  const canEdit = usePermissions().canEdit("role");
  const [openAddRoleModal, setOpenAddRoleModal] = useState<boolean>(false);
  const [changedPermissions, setChangedPermissions] =
    useState<UpdateGroupParams>({ permissions: [] });

  const {
    data: resources,
    isLoading: loadingResources,
    isError: errorLoadingResources,
  } = useGetAllResources();

  const {
    data: groups,
    isLoading: loadingGroups,
    isError: errorLoadingGroups,
  } = useGetAllGroups();

  const {
    data: actions,
    isLoading: loadingActions,
    isError: errorLoadingActions,
  } = useGetAllActions();

  const {
    data: groupResourceActionMapping,
    isLoading: loadingMapping,
    isError: errorMapping,
  } = useGetGroupResourceActionMapping();

  const {
    mutate: insertGroup,
    isSuccess: insertGroupIsSuccess,
    isPending: insertingGroup,
    isError: insertGroupIsError,
    error: insertGroupError,
  } = useInsertGroup();
  const {
    mutate: updateGroup,
    isSuccess: updateGroupIsSuccess,
    isPending: updatingGroup,
    isError: updateGroupIsError,
    error: updateGroupError,
  } = useUpdateGroup();

  useQueryHandler({
    isError:
      errorLoadingResources ||
      errorLoadingGroups ||
      errorLoadingActions ||
      errorMapping ||
      insertGroupIsError ||
      updateGroupIsError,
    isSuccess: updateGroupIsSuccess || insertGroupIsSuccess,
    error: updateGroupError || insertGroupError,
    errorMessage:
      getAxiosErrorMessage(updateGroupError) ||
      getAxiosErrorMessage(insertGroupError) ||
      "เกิดข้อผิดพลาด",
    successMessage: "บันทึกสิทธิ์การใช้งานสำเร็จ",
  });

  useEffect(() => {
    document.title = "Authorization Management | AMPA";
  }, []);

  const handleSave = () => {
    if (changedPermissions.permissions.length !== 0) {
      const existingPermissions: typeof changedPermissions.permissions = [];
      const newPermissions: typeof changedPermissions.permissions = [];

      changedPermissions.permissions.forEach((permission) => {
        const existingMapping = groupResourceActionMapping?.find(
          (mapping) => mapping.group.id === permission.group_id
        );

        if (existingMapping) {
          const existingResource = existingMapping.resources.find(
            (resource) => resource.resource.id === permission.resource_id
          );

          if (existingResource) {
            const existingAction = existingResource.actions.find(
              (action) => action.id === permission.action_id
            );

            if (existingAction) {
              existingPermissions.push(permission);
            } else {
              newPermissions.push(permission);
            }
          } else {
            newPermissions.push(permission);
          }
        } else {
          newPermissions.push(permission);
        }
      });

      if (existingPermissions.length > 0) {
        updateGroup({ permissions: existingPermissions });
      }

      if (newPermissions.length > 0) {
        insertGroup({ permissions: newPermissions });
      }
    }
    setChangedPermissions({ permissions: [] });
  };

  return (
    <Box sx={{ px: 3, py: 3 }}>
      <Breadcrumb current="Authorization" previous={[{ key: "role" }]} />
      <Box mt={1}>
        <Box
          display={"flex"}
          alignItems="center"
          justifyContent="flex-start"
          gap={2}
        >
          <BackButton to="/master-role" />
          <Typography variant="body1" fontWeight={600}>
            ปรับสิทธิ์การใช้งาน (Role & Authorization)
          </Typography>
        </Box>
        <Box mt={2} display="flex" justifyContent="space-between">
          <Box
            display="flex"
            alignItems="center"
            justifyContent="flex-start"
            mt={2}
            gap={1}
          >
            <Chip
              label={`Roles: ${groups?.length || 0}`}
              color="info"
              size="medium"
            />
            <Chip
              label={`Features: ${resources?.length || 0}`}
              color="info"
              size="medium"
            />
          </Box>
          <Box mt={2} display="flex" justifyContent="flex-end" mr={3} gap={2}>
            <Button
              variant="outlined"
              sx={DefaultSecondaryButton}
              onClick={() => setOpenAddRoleModal(true)}
              disabled={!canEdit}
            >
              เพิ่มบทบาทใหม่
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              sx={DefaultPrimaryButton}
              disabled={changedPermissions.permissions.length === 0 || !canEdit}
            >
              บันทึก
            </Button>
          </Box>
        </Box>
        <Box mt={2}>
          <AuthorizationManageTable
            resources={resources}
            groups={groups}
            actions={actions}
            groupResourceActionMapping={groupResourceActionMapping}
            setChangedPermissions={setChangedPermissions}
            isLoading={
              loadingResources ||
              loadingGroups ||
              loadingActions ||
              loadingMapping
            }
            isSaving={updatingGroup || insertingGroup}
          />
        </Box>
      </Box>

      <SaveRoleModal
        open={openAddRoleModal}
        handleClose={() => setOpenAddRoleModal(false)}
      />
    </Box>
  );
};

export default AuthorizationManagement;
